/**
 * Zoom segments in the editor: between `start` and `end` the video is enlarged around a focus
 * point, easing in and out. Applied in the draw step, so preview and export match. Annotations
 * stay on the full picture.
 */
export interface ZoomSegment {
  id: string;
  /** Seconds. */
  start: number;
  end: number;
  /** Zoom level (ZOOM_LIMITS). */
  scale: number;
  /** Focus point as a share of the video width / height (0 – 1). */
  x: number;
  y: number;
}

export interface ZoomView {
  scale: number;
  x: number;
  y: number;
}

export const ZOOM_LIMITS = { min: 1.25, max: 3 };
export const DEFAULT_ZOOM_SCALE = 2;
export const DEFAULT_ZOOM_LENGTH = 3;
export const MIN_ZOOM_LENGTH = 0.5;
/** Time to glide in and out (shorter segments use half their length). */
const EASE_SECONDS = 0.4;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const share = (value: unknown) => (Number.isFinite(Number(value)) ? clamp(Number(value), 0, 1) : 0.5);

/** Accepts a stored value (draft) and keeps only valid segments. */
export function normalizeZooms(value: unknown): ZoomSegment[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((v): ZoomSegment[] => {
    const z = (v && typeof v === 'object' ? v : {}) as Partial<ZoomSegment>;
    const start = Number(z.start);
    const end = Number(z.end);
    const scale = Number(z.scale);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
    return [{
      id: typeof z.id === 'string' && z.id ? z.id : `zoom-${Math.random().toString(36).slice(2)}`,
      start: Math.max(0, start),
      end,
      scale: Number.isFinite(scale) ? clamp(scale, ZOOM_LIMITS.min, ZOOM_LIMITS.max) : DEFAULT_ZOOM_SCALE,
      x: share(z.x),
      y: share(z.y),
    }];
  });
}

/**
 * Free time for a zoom starting at `start`: from the end of the zoom before it to the start of
 * the one after it (zooms never overlap). `exceptId` leaves out the zoom being changed.
 */
export function zoomLimits(zooms: ZoomSegment[], start: number, duration: number, exceptId?: string) {
  const others = zooms.filter((z) => z.id !== exceptId);
  const eps = 1e-6;
  return {
    lo: Math.max(0, ...others.filter((z) => z.end <= start + eps).map((z) => z.end)),
    hi: Math.min(duration, ...others.filter((z) => z.start >= start - eps).map((z) => z.start)),
  };
}

/** The zoom to draw at time `t`, or null when none is active. */
export function zoomAt(zooms: ZoomSegment[], t: number): ZoomView | null {
  const z = zooms.find((s) => t >= s.start && t <= s.end);
  if (!z) return null;
  const ease = Math.min(EASE_SECONDS, (z.end - z.start) / 2);
  const p = ease > 0 ? clamp(Math.min(t - z.start, z.end - t) / ease, 0, 1) : 1;
  const smooth = p * p * (3 - 2 * p);
  const scale = 1 + (z.scale - 1) * smooth;
  return scale > 1.001 ? { scale, x: z.x, y: z.y } : null;
}

/** Visible part of the video for a zoom, as shares (0 – 1): kept inside the picture. */
export function zoomViewport(view: ZoomView) {
  const w = 1 / view.scale;
  const h = 1 / view.scale;
  return {
    left: clamp(view.x - w / 2, 0, 1 - w),
    top: clamp(view.y - h / 2, 0, 1 - h),
    width: w,
    height: h,
  };
}
